import {
  useEffect,
  useCallback,
  type FormEvent,
  ReactElement,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import usePrevious from "src/hooks/usePrevious";
import * as colors from "src/styles/constants/colors";
import {
  getFilesDropdownUploadProgress,
  getFilesDropdownUploadedFile,
} from "src/reducers";
import { uploadFileInDropdown } from "src/actions/file";
import { TextArea, Button } from "src/components/Unifize";
import Display from "src/containers/file/Name";
import Icon from "src/icons";
import {
  PromptInput as StyledPromptInput,
  FileUploadContainer,
  FileItem,
  RemoveButton,
  UploadedFiles,
  FileInput,
} from "./styles";
import { Label } from "src/components/Unifize/Input/TextInput/styles";

import { type AppState } from "src/types";

type Props = {
  prompt: string;
  files: Array<string>;
  handleFile: (file: string) => void;
  handlePrompt: (event: FormEvent<HTMLTextAreaElement>) => void;
};

const PromptInput = ({
  prompt,
  files,
  handleFile,
  handlePrompt,
}: Props): ReactElement => {
  const dispatch = useDispatch();

  const uploadProgress = useSelector(({ app }: { app: AppState }) =>
    getFilesDropdownUploadProgress(app),
  );
  const uploadedFileId = useSelector(({ app }: { app: AppState }) =>
    getFilesDropdownUploadedFile(app),
  );
  const isUploading = uploadProgress && uploadProgress > 0;

  const previousUploadedFileId = usePrevious(uploadedFileId);

  const fileUploadRef = useRef(null);

  useEffect(() => {
    if (uploadedFileId && previousUploadedFileId !== uploadedFileId) {
      handleFile(uploadedFileId);
    }
  }, [previousUploadedFileId, uploadedFileId]);

  const handleFileSelector = useCallback(() => {
    if (fileUploadRef && fileUploadRef.current) {
      // @ts-ignore
      fileUploadRef.current.click();
    }
  }, [fileUploadRef]);

  const handleFileUpload = useCallback(
    (event: any) => {
      const fileData = event.target.files[0];
      dispatch(uploadFileInDropdown(fileData, dispatch));
    },
    [dispatch],
  );

  return (
    <div>
      <Label>Add prompt</Label>
      <StyledPromptInput>
        <TextArea
          placeholder="Describe the AI task or rules here. E.g., 'Suggest checklist steps based on the project description and priority.'"
          onChange={handlePrompt}
          value={prompt}
          style={{ border: "none", backgroundColor: "transparent" }}
          resize="none"
        />
        <FileUploadContainer>
          <UploadedFiles>
            {files.map((file, index) => (
              <FileItem key={index}>
                <Display name={file} />
                <RemoveButton onClick={() => handleFile(file)}>
                  <Icon type="decrement" width="16px" />
                </RemoveButton>
              </FileItem>
            ))}
          </UploadedFiles>
          <Button
            variant="default"
            handleClick={handleFileSelector}
            isLoading={!!isUploading}
            disabled={!!isUploading}
            color={colors.lightBlue600}
            border="solid 1px"
            bgColor={isUploading ? colors.lightBlue600 : "white"}
            borderRadius="4px"
          >
            <Icon type="uploadBlue" />
            <FileInput
              accept=".pdf,image/*"
              type="file"
              onChange={handleFileUpload}
              ref={fileUploadRef}
            />
            File Upload
          </Button>
        </FileUploadContainer>
      </StyledPromptInput>
    </div>
  );
};

export default PromptInput;
